import { useRuns } from '../../api/hooks';
import { SOURCE_LABELS } from '../../api/types';
import { Badge } from '../../components/Badge';
import { Card, CardHeader } from '../../components/Card';
import { EmptyState, QueryBoundary } from '../../components/states';
import { useFormatters } from '../../time/format';
import { formatDuration } from '../../time/useNow';

interface RunHistoryProps {
  enabled: boolean;
  /** Empty for every pollable source interleaved. */
  source: string;
}

/**
 * Recent polls, newest first.
 *
 * The question the cards above cannot answer. They say whether each source is alive right
 * now; this says whether it is behaving as it did yesterday, and the two come apart in the
 * cases that matter. A source whose polls have gone from one page to forty, or from 200ms to
 * two minutes, reports `ok` on every card and is in trouble — Red Canary once spent a poll
 * making 287 identical requests until its time budget ran out and reported success, and a
 * page count in a column would have made that obvious at a glance.
 *
 * A week deep. These rows carry a TTL because they are operational telemetry rather than the
 * incident record, which is the distinction that decides what expires in this table.
 */
export function RunHistory({ enabled, source }: RunHistoryProps) {
  const runs = useRuns(enabled, source);
  const { dateTime, precise } = useFormatters();

  const rows = [...(runs.data?.runs ?? [])].sort((a, b) =>
    b.started_at.localeCompare(a.started_at),
  );

  return (
    <Card className="p-0">
      <div className="p-5 pb-0">
        <CardHeader
          title="Run history"
          subtitle={
            source
              ? `${SOURCE_LABELS[source as keyof typeof SOURCE_LABELS] ?? source} — last 20 polls`
              : 'Last 20 polls per source, kept for seven days'
          }
        />
      </div>

      <QueryBoundary
        isLoading={runs.isPending}
        error={runs.error}
        onRetry={() => void runs.refetch()}
        loadingLabel="Loading run history"
      >
        {rows.length === 0 ? (
          <div className="p-5 pt-0">
            <EmptyState
              title="No polls recorded yet"
              detail="Runs appear here once the scheduler has fired. On a fresh deployment that is within a few minutes."
            />
          </div>
        ) : (
          <div className="scroll-thin overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-line text-xs uppercase tracking-wide text-subtle">
                <tr>
                  <th scope="col" className="px-5 py-3 font-semibold">
                    Started
                  </th>
                  <th scope="col" className="px-3 py-3 font-semibold">
                    Source
                  </th>
                  <th scope="col" className="px-3 py-3 font-semibold">
                    Result
                  </th>
                  <th scope="col" className="px-3 py-3 text-right font-semibold">
                    Duration
                  </th>
                  <th scope="col" className="px-3 py-3 text-right font-semibold">
                    Ingested
                  </th>
                  <th scope="col" className="px-3 py-3 text-right font-semibold">
                    Pages
                  </th>
                  <th scope="col" className="px-5 py-3 font-semibold">
                    Trigger
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {rows.map((run) => (
                  <tr key={`${run.source}-${run.started_at}`}>
                    <td
                      className="whitespace-nowrap px-5 py-2.5 text-xs text-muted"
                      title={precise(run.started_at)}
                    >
                      {dateTime(run.started_at)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs text-muted">
                      {SOURCE_LABELS[run.source] ?? run.source}
                    </td>
                    <td className="px-3 py-2.5">
                      <Badge tone={run.ok ? 'success' : 'danger'}>{run.ok ? 'ok' : 'failed'}</Badge>
                      {run.error ? (
                        <span className="ml-2 font-mono text-[11px] text-danger-fg">
                          {run.error}
                        </span>
                      ) : null}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right text-xs tabular-nums text-muted">
                      {formatDuration(run.duration_ms)}
                    </td>
                    <td className="px-3 py-2.5 text-right text-xs tabular-nums text-muted">
                      {run.ingested === 0 ? <span className="text-subtle">—</span> : run.ingested}
                    </td>
                    <td className="px-3 py-2.5 text-right text-xs tabular-nums text-muted">
                      {run.pages}
                    </td>
                    <td className="px-5 py-2.5 text-xs text-muted">
                      {/*
                        A manual run explains a gap in the scheduled cadence that would
                        otherwise read as a missed tick, which is why the distinction is
                        recorded rather than inferred from the timing.
                      */}
                      {run.trigger === 'scheduled' ? (
                        <span className="text-subtle">scheduled</span>
                      ) : (
                        <Badge tone="info">{run.trigger || 'unknown'}</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryBoundary>
    </Card>
  );
}
